import classNames from "classnames";
import React, { forwardRef } from "react";
import Icon from "../Icon";

import "./Pill.css"
/**
 * Description of the component.
 */

const baseClass = "wb-pill"

type PillProps = {
  /**
   * Passthrough classname for customising styling of the pill.
   */
  className?: string;
  /**
   * The color of the pill.
   */
  color?: 'primary' | 'secondary_one' | 'secondary_two' | 'white' | 'info' | 'success' | 'warning';
  /**
   * The size of the pill.
   */
  size?: 'small' | 'medium' | 'large';
  /**
   * Shape of the pill.
   */
  shape?: 'round' | 'square';
  /**
   * Icon that will show before the children/text. If the icon name is provided, this will pass through to the Icon component.
   */
  iconBefore?: React.ReactNode;
  /**
   * Icon that will show after the children/text. If the icon name is provided, this will pass through to the Icon component.
   */
  iconAfter?: React.ReactNode;
  /**
   * Callback function for when the pill is clicked. If this is provided, the pill will be clickable.
   */
  onClick?: () => void;
  /**
   * The pill's contents.
   */
  children?: React.ReactNode;
};

const Pill = forwardRef<HTMLDivElement, PillProps>(({
  className,
  color = 'primary',
  size = 'medium',
  shape = 'round',
  iconBefore,
  iconAfter,
  onClick,
  children
}, ref) => {
  return (
    <div ref={ref}
      className={classNames(baseClass, className, {
        [`${baseClass}--${color}`]: color,
        [`${baseClass}--${size}`]: size,
        [`${baseClass}--${shape}`]: shape,
        [`${baseClass}--icon`]: iconBefore || iconAfter,
        [`${baseClass}--clickable`]: onClick,
      })}
      onClick={onClick}
    >
      {iconBefore &&
        /* The icon passed is named and therefore should be passed to the Icon component. */
        typeof iconBefore == "string" ? (
        <Icon name={iconBefore} className={`${baseClass}--icon-before`} />
      ) : /* The icon can be treated as a child. */ (
        <div className={classNames(
          `${baseClass}--icon-before`,
          `${baseClass}--icon-container`)}>{iconBefore}</div>
      )
      }
      {children && <div className={`${baseClass}--content`}>{children}</div>}
      {iconAfter &&
        /* The icon passed is named and therefore should be passed to the Icon component. */
        (typeof iconAfter == "string" ? (
          <Icon name={iconAfter} className={`${baseClass}--icon-after`} />
        ) : /* The icon can be treated as a child. */ (
          <div className={classNames(
            `${baseClass}--icon-after`,
            `${baseClass}--icon-container`)}>{iconAfter}</div>
        ))
      }
    </div>
  )
})

export { Pill }
export type { PillProps }
